import axios from 'axios';
import { compressImage } from '../utils/imageCompression';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 - let AuthContext handle authentication state
    // This prevents infinite redirect loops
    return Promise.reject(error);
  }
);

// API types
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
}

export interface User {
  id: string;
  name: string;
  level: number;
}

export interface RoomInfo {
  room_id: string;
  building: string;
  room_number: string;
  room_address: string;
  room_type: string;
}

export interface CleaningItem {
  id: number;
  date: string;
  roomId: string;
  userId: string | null;
  userName: string | null;
  selectedTime: string | null;
  completed: boolean;
  completedTime: string | null;
  canceled: boolean;
  noshow: boolean;
  isJaesil: boolean;
  roomInfo?: RoomInfo;
  checkinMemo?: string;
  checkoutMemo?: string;
  memoCount?: number;
  password?: string;
}

export interface MemoImage {
  imageUrl: string;
  thumbnailUrl: string;
}

export interface MemoComment {
  id: number;
  userId: string;
  comment: string;
  createdAt: string;
  isRead: boolean;
  readAt: string | null;
  user: {
    id: string;
    name: string;
  };
  images: MemoImage[];
}

export interface ProgressStats {
  total: number;
  completed: number;
  canceled: number;
  percentage: number;
}

export interface CleaningCheck {
  id: number;
  checked: boolean;
  cleaningItem: {
    id: number;
    itemName: string;
  };
}

export interface InspectionData {
  id: number;
  date: string;
  roomId: string;
  userId: string | null;
  completed: boolean;
  canceled: boolean;
  noshow: boolean;
  isJaesil: boolean;
  user?: {
    id: string;
    name: string;
  };
  cleaningChecks?: CleaningCheck[];
  roomInfo?: RoomInfo;
}

export interface Memo {
  id: number;
  roomId: string;
  memo: string;
  createdAt: string;
  updatedAt: string;
}

export interface DirectMessage {
  id: number;
  date: string;
  senderId: string;
  receiverId: string;
  message: string;
  createdAt: string;
  isRead: boolean;
  readAt: string | null;
  sender: {
    id: string;
    name: string;
  };
}

export interface DailyNotice {
  id: number;
  date: string;
  notice: string;
  createdAt: string;
  updatedAt: string;
}

export interface CleaningListResponse {
  date: string;
  list: CleaningItem[];
  stats: ProgressStats;
  unlockStatus: boolean;
}

// Auth API
export const authApi = {
  login: (id: string, password: string) =>
    apiClient.post<ApiResponse<{ user: User }>>('/auth/login', { id, password }),

  register: (id: string, password: string, name: string, inviteCode: string) =>
    apiClient.post<ApiResponse<{ user: User }>>('/auth/register', {
      id,
      password,
      name,
      inviteCode,
    }),

  logout: () => apiClient.post<ApiResponse>('/auth/logout'),

  me: () => apiClient.get<ApiResponse<{ user: User }>>('/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiClient.put<ApiResponse>('/auth/change-password', {
      currentPassword,
      newPassword,
    }),

  getUsers: () => apiClient.get<ApiResponse<{ users: User[] }>>('/auth/users'),
};

// Cleaning API
export const cleaningApi = {
  getList: (date?: string, userId?: string) =>
    apiClient.get<ApiResponse<CleaningListResponse>>('/cleaning/list', {
      params: { date, userId },
    }),

  toggleRoom: (listId: number) =>
    apiClient.post<ApiResponse<{ selected: boolean }>>('/cleaning/toggle-room', {
      listId,
    }),

  toggleComplete: (listId: number) =>
    apiClient.post<ApiResponse<{ completed: boolean }>>('/cleaning/toggle-complete', {
      listId,
    }),

  toggleCancel: (listId: number) =>
    apiClient.post<ApiResponse<{ canceled: boolean }>>('/cleaning/toggle-cancel', {
      listId,
    }),

  toggleNoshow: (listId: number) =>
    apiClient.post<ApiResponse<{ noshow: boolean }>>('/cleaning/toggle-noshow', {
      listId,
    }),

  toggleUnlock: (date: string) =>
    apiClient.post<ApiResponse<{ unlocked: boolean }>>('/cleaning/toggle-unlock', {
      date,
    }),

  getInspection: (listId: number) =>
    apiClient.get<ApiResponse<{ inspection: InspectionData }>>(`/cleaning/inspection/${listId}`),

  updateCheck: (checkId: number, checked: boolean) =>
    apiClient.post<ApiResponse>('/cleaning/update-check', { checkId, checked }),

  getStats: (date?: string, userId?: string) =>
    apiClient.get<ApiResponse<{ overall: ProgressStats; user: ProgressStats | null }>>(
      '/cleaning/stats',
      { params: { date, userId } }
    ),
};

// Memo API
export const memoApi = {
  getMemos: (date?: string, roomId?: string) =>
    apiClient.get<ApiResponse<{ memos: Memo[] }>>('/memos', {
      params: { date, roomId },
    }),

  saveMemo: (date: string, roomId: string, memo: string) =>
    apiClient.post<ApiResponse>('/memos/save', { date, roomId, memo }),

  getComments: (date: string, roomId: string) =>
    apiClient.get<ApiResponse<{ comments: MemoComment[] }>>('/memos/comments', {
      params: { date, roomId },
    }),

  addComment: (date: string, roomId: string, comment: string) =>
    apiClient.post<ApiResponse<{ comment: MemoComment }>>('/memos/comment', { date, roomId, comment }),

  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.post<ApiResponse<{ imageUrl: string; thumbnailUrl: string }>>(
      '/memos/upload-image',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  // 이미지와 함께 댓글 추가
  addCommentWithImage: async (date: string, roomId: string, comment: string, imageFile?: File) => {
    let imageUrl: string | undefined;
    let thumbnailUrl: string | undefined;

    // 이미지가 있으면 압축 후 업로드
    if (imageFile) {
      try {
        // 이미지 압축 (1200x1200, 80% 품질)
        const compressedBlob = await compressImage(imageFile);
        const compressedFile = new File([compressedBlob], 'compressed.jpg', { type: 'image/jpeg' });

        const formData = new FormData();
        formData.append('image', compressedFile);
        const uploadRes = await apiClient.post<ApiResponse<{ imageUrl: string; thumbnailUrl: string }>>(
          '/memos/upload-image',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (uploadRes.data.success && uploadRes.data.data) {
          imageUrl = uploadRes.data.data.imageUrl;
          thumbnailUrl = uploadRes.data.data.thumbnailUrl;
        }
      } catch (error) {
        console.error('이미지 압축 실패:', error);
        // 압축 실패 시 원본 이미지로 업로드 시도
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadRes = await apiClient.post<ApiResponse<{ imageUrl: string; thumbnailUrl: string }>>(
          '/memos/upload-image',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (uploadRes.data.success && uploadRes.data.data) {
          imageUrl = uploadRes.data.data.imageUrl;
          thumbnailUrl = uploadRes.data.data.thumbnailUrl;
        }
      }
    }

    // 댓글 추가 (이미지 URL 포함)
    return apiClient.post<ApiResponse<{ comment: MemoComment }>>('/memos/comment', {
      date,
      roomId,
      comment,
      imageUrl,
      thumbnailUrl,
    });
  },
};

// Message API
export const messageApi = {
  getMessages: (date?: string) =>
    apiClient.get<ApiResponse<{ messages: DirectMessage[] }>>('/messages', {
      params: { date },
    }),

  getReceivedMessages: (date?: string) =>
    apiClient.get<ApiResponse<{ messages: DirectMessage[] }>>('/messages/received', {
      params: { date },
    }),

  sendMessage: (receiverId: string, message: string, date?: string) =>
    apiClient.post<ApiResponse>('/messages/send', { receiverId, message, date }),

  markAsRead: (messageId: number) =>
    apiClient.put<ApiResponse>('/messages/read', { messageId }),

  getUnreadCount: () =>
    apiClient.get<ApiResponse<{ count: number }>>('/messages/unread-count'),
};

// Notice API
export const noticeApi = {
  getNotice: (date?: string) =>
    apiClient.get<ApiResponse<{ notice: DailyNotice | null }>>('/notices', {
      params: { date },
    }),

  saveNotice: (date: string, notice: string) =>
    apiClient.post<ApiResponse>('/notices/save', { date, notice }),
};

// Checkout API
export const checkoutApi = {
  getStatus: (date?: string) =>
    apiClient.get<ApiResponse<{ checkedOut: boolean; checkoutTime: string | null }>>(
      '/checkout/status',
      { params: { date } }
    ),

  toggle: (date?: string) =>
    apiClient.post<ApiResponse<{ checkedOut: boolean; checkoutTime: string | null }>>(
      '/checkout/toggle',
      { date }
    ),

  getAll: (date?: string) =>
    apiClient.get<ApiResponse<{ statuses: unknown[] }>>('/checkout/all', {
      params: { date },
    }),
};

// Export API
export const exportApi = {
  downloadExcel: (startDate: string, endDate: string) =>
    apiClient.get('/export/excel', {
      params: { startDate, endDate },
      responseType: 'blob',
    }),
};

// Invite Code types
export interface InviteCode {
  id: number;
  code: string;
  level: number;
  isUsed: boolean;
  usedBy: string | null;
  usedAt: string | null;
  createdAt: string;
  usedByUser?: {
    id: string;
    name: string;
  } | null;
}

// Admin API
export const adminApi = {
  // 초대 코드 생성
  createInviteCodes: (level: number, count: number = 1) =>
    apiClient.post<ApiResponse<{ codes: string[] }>>('/admin/invite-codes', {
      level,
      count,
    }),

  // 초대 코드 목록 조회
  getInviteCodes: () =>
    apiClient.get<ApiResponse<{ codes: InviteCode[] }>>('/admin/invite-codes'),

  // 초대 코드 삭제
  deleteInviteCode: (code: string) =>
    apiClient.delete<ApiResponse>(`/admin/invite-codes/${code}`),
};
